import { TRANSPORT_REASON_MAX, type TransportNocTripRow, makeTransportBulkStatusRequest } from "@seagames/contracts";
import { Field, Modal, ModalActions } from "@seagames/ui";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { bulkTripStatus } from "../../lib/api/transport";
import { invalidateTrips } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { useTransportFormMessages as useTripFormMessages } from "../../lib/i18n/use-form-schemas";

/**
 * Cancel a venue trip (her NOC cancel sheet: "Cancel this trip · The driver
 * is released"). A reason is required — the host sees it. The rule (its own
 * ADHOC trip, OPEN, not started) is the API's: `can_cancel` only decides
 * whether the button shows; the bulk endpoint re-checks in its WHERE and a
 * skipped row comes back as a reason, never a silent success.
 */
export function CancelTripModal({ trip, onClose }: { trip: Pick<TransportNocTripRow, "transport_request_id" | "ref">; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const messages = useTripFormMessages();
	const [reason, setReason] = useState("");
	const [error, setError] = useState<string | undefined>();
	const [busy, setBusy] = useState(false);
	const field = useRef<HTMLTextAreaElement>(null);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const parsed = makeTransportBulkStatusRequest(messages).safeParse({ ids: [trip.transport_request_id], action: "cancel", reason });
		if (!parsed.success) {
			setError(parsed.error.issues.find((i) => i.path[0] === "reason")?.message ?? parsed.error.issues[0]?.message);
			return;
		}
		setBusy(true);
		try {
			const res = await bulkTripStatus(parsed.data);
			await invalidateTrips();
			const skipped = res.skipped[0];
			if (skipped) {
				toast.error(new Error(t(`noc.cancelSkipped.${skipped.reason}`)), t("noc.cancelFailed"));
				onClose();
				return;
			}
			toast.success(t("noc.cancelled", { ref: trip.ref }));
			onClose();
		} catch (err) {
			toast.error(err, t("noc.cancelFailed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="sm"
			title={t("noc.cancelTitle")}
			description={t("noc.cancelDescription", { ref: trip.ref })}
			dismissible={!busy}
			initialFocusRef={field}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{t("noc.back")}
					</button>
					<button type="submit" form="cancel-trip-form" className="btn btn-error btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("noc.cancelConfirm")}
					</button>
				</ModalActions>
			}
		>
			<form id="cancel-trip-form" onSubmit={(e) => void submit(e)} noValidate>
				<Field label={t("noc.reason")} htmlFor="cancel-trip-reason" required error={error}>
					<textarea
						ref={field}
						id="cancel-trip-reason"
						rows={3}
						maxLength={TRANSPORT_REASON_MAX}
						value={reason}
						onChange={(e) => {
							setReason(e.target.value);
							setError(undefined);
						}}
						placeholder={t("noc.reasonPlaceholder")}
						className={`textarea textarea-sm w-full ${error ? "textarea-error" : ""}`}
					/>
				</Field>
			</form>
		</Modal>
	);
}
